import { Combobox, Transition } from "@headlessui/react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconFilter,
  IconSearch,
  IconSearchOff,
} from "@tabler/icons-react";
import clsx from "clsx";
import Checkbox from "components/Checkbox";
import DebouncedInput from "components/DebounceInput";
import Tooltip from "components/Tooltip";
import { observer } from "mobx-react-lite";
import React, { ReactNode, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { globalStore } from "store/GlobalStore";

export interface DropdownOption {
  id?: string | number;
  name: string;
  name_de?: string | null;
  icon?: ReactNode;
  title?: string;
  title_de?: string;
  value?: string;
  children?: DropdownOption[];
  badge?: string;
  disabled?: boolean;
  isCategory?: boolean;
  parent?: DropdownOption;
  hidden?: boolean;
}

interface ChildrenOptions {
  count: number;
  items: DropdownOption[];
}

interface FilterMenuProps {
  options: DropdownOption[];
  wrapperID?: string;
  fetchChildren?: (
    id?: string,
    searchTerm?: string,
    pageNumber?: number,
    pageSize?: number,
    update?: boolean
  ) => Promise<ChildrenOptions | undefined>;
  onSelectItem?: (items: SelectedItems[]) => void;
  checkedItems?: SelectedItems[];
  pageSize?: number;
  noResultText?: string;
  disabled?: boolean;
  singleSelect?: boolean;
}

export interface SelectedItems {
  category: DropdownOption;
  items: DropdownOption[];
}

const FiltersMenu = ({ pageSize = 25, ...props }: FilterMenuProps) => {
  const { t } = useTranslation();
  const wrapperEl = document.getElementById(props.wrapperID as string);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [hasMoreOptions, setHasMoreOptions] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const isEnglish = globalStore.currentLanguage === "en";
  const [childrenOptions, setChildrenOptions] = React.useState<ChildrenOptions>(
    { count: 0, items: [] }
  );
  const [selectedItems, setSelectedItems] = React.useState<SelectedItems[]>([]);
  const [navigationPath, setNavigationPath] = React.useState<DropdownOption[]>(
    []
  );

  const [activeCategory, setActiveCategory] =
    React.useState<DropdownOption | null>();

  React.useEffect(() => {
    props.checkedItems && setSelectedItems(props.checkedItems);
  }, [props.checkedItems]);

  React.useEffect(() => {
    return () => clearStates();
  }, [isMenuOpen]);

  React.useEffect(() => {
    onChangeQuery();
  }, [searchTerm]);

  function clearStates() {
    setActiveCategory(null);
    setChildrenOptions({ count: 0, items: [] });
    setNavigationPath([]);
    setSearchTerm("");
  }

  async function onChangeQuery() {
    setLoading(true);
    setActiveCategory(null);
    await getChildrenOptions?.(activeCategory, 1, true);
    setLoading(false);
  }

  async function onClickedNodeWithChildren(
    item: DropdownOption,
    update?: boolean
  ) {
    if (!item.id || searchTerm) return;
    setLoading(true);
    setActiveCategory(item);

    setNavigationPath((state) => [...state, item]);
    await getChildrenOptions(item, 1, update);
    setLoading(false);
  }

  async function getChildrenOptions(
    parent?: DropdownOption | null,
    pageNumber?: number,
    update?: boolean
  ) {
    const res = await props.fetchChildren?.(
      String(parent?.id),
      searchTerm,
      pageNumber ?? childrenOptions.items.length / pageSize + 1,
      pageSize,
      update
    );

    if (!res) return;
    const itemsList = update
      ? res?.items
      : [...childrenOptions.items, ...res.items];

    setChildrenOptions({
      count: res.count,
      items: itemsList,
    });
    setHasMoreOptions(itemsList.length !== res.count);
  }

  function onClickedChild(clickedItem: DropdownOption, state: boolean) {
    let currentSelectList = [...selectedItems];

    const parent = activeCategory ?? clickedItem.parent;

    const itemCategory = currentSelectList.find(
      (rec) => rec.category.id === parent?.id
    );

    if (state && parent) {
      if (itemCategory) {
        if (props.singleSelect) {
          itemCategory.items = [clickedItem];
        } else {
          itemCategory.items.push(clickedItem);
        }
      } else {
        currentSelectList.push({
          category: parent,
          items: [clickedItem],
        });
      }
    } else if (!state && itemCategory) {
      const filteredItems = itemCategory?.items.filter(
        (rec) => String(rec.id) !== String(clickedItem.id)
      );
      if (filteredItems.length) {
        itemCategory.items = filteredItems;
      } else {
        currentSelectList = currentSelectList.filter(
          (rec) => rec.category?.id !== parent?.id
        );
      }
    }

    props.onSelectItem?.([...currentSelectList]);
  }

  function searchField() {
    return (
      <div className="sticky top-0 p-4 z-10 bg-white">
        <div
          className={clsx(
            "bg-white border-gray-300 border rounded-md",
            "flex justify-between px-2 items-center text-gray-900",
            {
              "opacity-50 !cursor-not-allowed pointer-events-none":
                props.disabled,
            }
          )}
        >
          <DebouncedInput
            className={clsx(
              "w-full pl-0 placeholder:text-gray-500 text-sm leading-5 ring-0 outline-none border-none focus:ring-0 rounded-md"
            )}
            inputWrapperClassName="border-none"
            onChange={(value) => setSearchTerm(String(value))}
            value={searchTerm}
            placeholder={t("commons.search")}
            icon={<></>}
          />
          <IconSearch width={24} className="text-gray-700" />
        </div>
      </div>
    );
  }

  async function onClickBack(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    e.stopPropagation();
    if (!activeCategory?.isCategory) {
      navigationPath.pop();
      const parent = navigationPath.at(-1);
      setActiveCategory(parent);
      parent && (await getChildrenOptions(parent, 1, true));
      return;
    }
    setNavigationPath([]);
    setActiveCategory(null);
    setSearchTerm("");
    setChildrenOptions({ count: 0, items: [] });
  }

  async function onScrollLoadMore(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    e.stopPropagation();
    if (!hasMoreOptions || !activeCategory?.id) return;
    if (e.target) {
      const { scrollHeight, scrollTop, clientHeight } =
        e.target as HTMLDivElement;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        await getChildrenOptions(activeCategory);
      }
    }
  }

  function hasCategoryAnySelection(categoryID: string | number | undefined) {
    const currentCategory = selectedItems?.find(
      (rec) => rec.category?.id === categoryID
    );
    return currentCategory ?? false;
  }

  function isItemChecked(item: DropdownOption) {
    if (searchTerm) {
      const itemExist = selectedItems
        .map((rec) => rec.items)
        .find((rec) => rec.find((rec) => rec.id === item.id));
      return !!itemExist;
    }

    return (
      selectedItems
        .find((rec) => rec.category?.id === activeCategory?.id)
        ?.items.map((rec) => String(rec.id))
        ?.includes(String(item.id)) ?? false
    );
  }

  function comboOption(item: DropdownOption) {
    return item.isCategory ? parentItem() : childItem();

    function parentItem(): React.ReactElement {
      return (
        <Combobox.Option
          key={item.id}
          className={clsx(
            "text-gray-700 cursor-pointer min-w-[320px]",
            "hover:bg-indigo-50 hover:text-indigo-700",
            "flex justify-between items-center gap-6 text-sm font-medium px-4 py-2",
            {
              "!opacity-50 pointer-events-none cursor-not-allowed":
                item.disabled,
            }
          )}
          value={item}
          onClick={() => onClickedNodeWithChildren(item)}
          disabled={item.disabled}
        >
          <span className="whitespace-nowrap">
            {isEnglish ? item.name : item.name_de ?? item.name}
          </span>
          {arrowIcon(item)}
        </Combobox.Option>
      );
    }

    function childItem(): React.ReactElement {
      return (
        <div
          key={item.id}
          className={clsx(
            "flex justify-between items-center text-sm font-medium px-4 py-2",
            "hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer group/option",
            {
              "opacity-50 !cursor-not-allowed pointer-events-none":
                props.disabled,
            }
          )}
          onClick={(e) => {
            if (item.children?.length && !item.isCategory) {
              e.preventDefault();
              onClickedNodeWithChildren(item, true);
            }
          }}
        >
          <div className="flex w-full">
            <div className="flex w-full">
              <Checkbox
                id={item.id as string}
                onChange={(e) => onClickedChild(item, e.currentTarget.checked)}
                containerClassName="!py-0 mr-3"
                checked={isItemChecked(item)}
                disabled={props.disabled}
              />
              <div>
                <Tooltip
                  content={isEnglish ? item.name : item.name_de ?? item.name}
                  truncate
                  className="max-w-[230px]"
                >
                  {isEnglish ? item.name : item.name_de ?? item.name}
                </Tooltip>
                {item.parent && (
                  <div className="text-gray-500 text-sm font-normal group-hover/option:text-indigo-500">
                    {item.parent.name}
                  </div>
                )}
              </div>
            </div>
            {arrowIcon(item)}
          </div>
          {item.badge == "HIGH_QUALITY" && (
            <div className="text-xs rounded-full px-2 mr-1 bg-green-100 text-green-700 whitespace-nowrap">
              {t("productEditor.highDataQuality")}
            </div>
          )}
        </div>
      );
    }
  }

  function arrowIcon(item: DropdownOption) {
    if (!item.children?.length || searchTerm) return null;
    return (
      <div className="flex items-center">
        {hasCategoryAnySelection(item.id) && (
          <span className="text-gray-500 pr-2 font-normal text-nowrap">
            {t("commons.filterSet")}
          </span>
        )}
        <IconChevronRight size={20} />
      </div>
    );
  }

  function noSearchResult() {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[300px]">
        <IconSearchOff size={57} className="text-gray-200 mb-4" />
        <div className="text-gray-500 text-xs font-medium text-center">
          {props.noResultText}
        </div>
      </div>
    );
  }

  function showOptions() {
    if (activeCategory && childrenOptions.items.length === 0) {
      return noSearchResult();
    }
    const currentList =
      activeCategory || searchTerm ? childrenOptions.items : props.options;
    return currentList.map((item) => comboOption(item));
  }

  function menuContent(): React.ReactNode {
    return (
      <Transition
        as={"div"}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={clsx(
            "absolute z-20 my-1 w-96 right-0 min-w-[320px] overflow-hidden",
            "rounded-md bg-white text-base !p-0",
            "shadow-lg border border-gray-300 min-w-auto w-auto max-w-md focus:outline-none sm:text-sm"
          )}
        >
          <Combobox.Options>
            <div className="sticky top-0">
              {searchField()}
              {activeCategory && (
                <div
                  className={clsx(
                    "text-gray-500 flex items-center pl-2 pr-4 py-[6px] border-y border-gray-300",
                    "cursor-pointer bg-gray-50 group/menu relative"
                  )}
                  onClick={(e) => onClickBack(e)}
                >
                  <IconChevronLeft
                    size={24}
                    className="group-hover/menu:text-indigo-700"
                  />
                  <div className="text-center text-sm font-semibold w-full">
                    {isEnglish
                      ? activeCategory?.name
                      : activeCategory?.name_de ?? activeCategory?.name}{" "}
                    {t("productEditor.isTheSame")}
                  </div>
                </div>
              )}
            </div>

            <div
              className={clsx(
                "overflow-auto",
                activeCategory ? "max-h-[290px]" : "max-h-[420px]"
              )}
              onScroll={onScrollLoadMore}
            >
              {loading ? skeleton() : showOptions()}
            </div>

            {activeCategory && hasMoreOptions && !loading && (
              <div className="py-1 bg-white px-4 text-gray-500 border-t border-gray-300 sticky bottom-0 w-full">
                {t("commons.scrollToLoadMore")}
              </div>
            )}
          </Combobox.Options>
        </div>
      </Transition>
    );
  }

  return (
    <div className="rounded-md">
      <Combobox multiple>
        <Combobox.Button>
          {({ open }) => {
            useEffect(() => {
              setIsMenuOpen(open);
            }, [open]);
            return (
              <div
                className={clsx(
                  "border border-gray-300 rounded-md flex justify-between h-10 items-center px-2",
                  "text-base text-gray-700 font-medium cursor-pointer"
                )}
              >
                <IconFilter className="mr-2" />
                {t("commons.filter")}
              </div>
            );
          }}
        </Combobox.Button>
        {wrapperEl
          ? ReactDOM.createPortal(menuContent(), wrapperEl)
          : menuContent()}
      </Combobox>
    </div>
  );
};

export default observer(FiltersMenu);

function skeleton() {
  const divs = new Array(9)
    .fill(null)
    .map((_, index) => (
      <div
        key={index}
        className="h-7 w-full bg-gray-200 rounded-lg dark:bg-gray-700 my-1"
      ></div>
    ));

  return <div className="animate-pulse px-4 py-2">{divs}</div>;
}
